import LeaderboardTable from "./LeaderboardTable/LeaderboardTable";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import rightArrow from "../../../assets/Icons/arrow.svg";
import { roleToggler } from "../../../redux-toolkit/features/access/accessSlice";
import DateRangePickerComp from "../../UI/DateRangePicker";
import "./SaDashboard.css";
import { Graph } from "../../Admin/Dashboard/Charts/Graph";
import { QuestActivity } from "../../Admin/Dashboard/Quest Activity/QuestActivity";

import {
  useBadgeStatsQuery,
  useBookDataQuery,
  useUserStatsQuery,
  useBookStatsQuery,
} from "../../../redux-toolkit/features/dashboard/dashboardApliSlice";
import ProgressBar from "../../UI/ProgressBar";
import { useGetAllCompaniesQuery } from "../../../redux-toolkit/features/company/companyApiSlice";
import { setCompany } from "../../../redux-toolkit/features/dashboard/dashboardSlice";
import {
  setActiveItem,
  setSelectBooks,
} from "../../../redux-toolkit/features/theme/themeSlice";
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const resolveScopedBookId = (selectedBook, books = []) => {
  if (selectedBook?._id && books.some((book) => book?._id === selectedBook._id)) {
    return selectedBook._id;
  }
  return books[0]?._id;
};

const booksOfType = (books = [], type) =>
  (books || []).filter(
    (book) => String(book?.type || "").toUpperCase() === String(type).toUpperCase()
  );

const emptyBookActivityStats = (totalEmployees = 0) => ({
  data: {
    _id: "3",
    title: "User Activity Overview",
    dataValue: [0, 0, 0],
    backgroundColor: ["#99c551", "#8a73db", "#ffbe18"],
    details: [
      { color: "#99c551", label: "Completed", values: 0 },
      { color: "#8a73db", label: "In Progress", values: 0 },
      { color: "#ffbe18", label: "Not Started", values: 0 },
    ],
    footerLabel: `Total employees: ${totalEmployees}`,
    attemptedBooks: [],
  },
});

const totalEmployeesFromUserStats = (userStats) => {
  const match = String(userStats?.data?.footerLabel || "").match(
    /Total employees:\s*(\d+)/i
  );
  return match ? Number(match[1]) : 0;
};

export const SaDashboard = () => {
  const dispatch = useDispatch();
  const selectBooks = useSelector((state) => state.theme.selectBooks);
  const [openCompanySelect, setOpenCompanySelect] = React.useState();
  const [dateRange, setDateRange] = React.useState(null);
  React.useEffect(() => {
    dispatch(roleToggler("Admin"));
  }, []);
  const { companyId } = useSelector((state) => state.auth.user);
  const { selectedCompany } = useSelector((state) => state.dashboard);
  const [value, setValue] = React.useState(0);
  const shouldSkipCompanyQueries = value !== 1 || !selectedCompany?._id;

  // Organizationals
  const { data: bookOrg, isLoading } = useBookDataQuery(
    { type: "org", userType: "superadmin" },
    { refetchOnMountOrArgChange: true }
  );

  const { data: badgeStatsOrg, isLoading: badgeStatsOrgLoading } =
    useBadgeStatsQuery(
      {
        userType: "superadmin",
        type: "org",
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      { refetchOnMountOrArgChange: true }
    );
  const { data: userStatsOrg, isLoading: userStatsOrgLoading } =
    useUserStatsQuery(
      {
        userType: "superadmin",
        type: "org",
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      { refetchOnMountOrArgChange: true }
    );
  const orgBooks = React.useMemo(
    () => booksOfType(bookOrg?.data, "ORG"),
    [bookOrg?.data]
  );
  const bookIdForOrgStats = resolveScopedBookId(selectBooks, orgBooks);
  const shouldSkipBookStatsOrg = value !== 0 || !bookIdForOrgStats;
  const {
    data: bookStatsOrg,
    isFetching: bookStatsOrgFetching,
  } = useBookStatsQuery(
      {
        bookId: bookIdForOrgStats,
        userType: "superadmin",
        type: "org",
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: shouldSkipBookStatsOrg,
        keepPreviousData: true,
      }
    );
  //  Company
  const { data: bookCompany, isLoading: companyLoading } = useBookDataQuery(
    {
      companyId: selectedCompany?._id,
      type: "company",
      userType: "superadmin",
    },
    { refetchOnMountOrArgChange: true, skip: shouldSkipCompanyQueries }
  );

  const { data: badgeStatsCompany, isLoading: badgeStatsCompanyLoading } =
    useBadgeStatsQuery(
      {
        userType: "superadmin",
        type: "company",
        companyId: selectedCompany?._id,
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      { refetchOnMountOrArgChange: true, skip: shouldSkipCompanyQueries }
    );
  const { data: userStatsCompany, isLoading: userStatsCompanyLoading } =
    useUserStatsQuery(
      {
        userType: "superadmin",
        type: "company",
        companyId: selectedCompany?._id,
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      { refetchOnMountOrArgChange: true, skip: shouldSkipCompanyQueries }
    );
  const companyBooks = React.useMemo(
    () => booksOfType(bookCompany?.data, "COMPANY"),
    [bookCompany?.data]
  );
  const bookIdForCompanyStats = resolveScopedBookId(selectBooks, companyBooks);
  const shouldSkipBookStatsCompany =
    shouldSkipCompanyQueries || !bookIdForCompanyStats;
  const {
    data: bookStatsCompany,
    isFetching: bookStatsCompanyFetching,
  } = useBookStatsQuery(
      {
        bookId: bookIdForCompanyStats,
        userType: "superadmin",
        type: "company",
        companyId: selectedCompany?._id,
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: shouldSkipBookStatsCompany,
        keepPreviousData: true,
      }
    );
  const {
    data: allCompanies,
    isLoading: companyloading,
  } = useGetAllCompaniesQuery();

  React.useEffect(() => {
    dispatch(roleToggler("Super Admin"));

    dispatch(setActiveItem(1));
  }, []);

  React.useEffect(() => {
    if (value === 0) {
      if (!orgBooks.length) {
        if (selectBooks?._id) {
          dispatch(setSelectBooks({}));
        }
        return;
      }

      const hasSelectedBook =
        !!selectBooks?._id &&
        orgBooks.some((book) => book?._id === selectBooks._id);

      if (!hasSelectedBook) {
        dispatch(
          setSelectBooks({
            _id: orgBooks[0]._id,
            name: orgBooks[0]?.name,
          })
        );
      }
      return;
    }

    if (!companyBooks.length) {
      if (selectBooks?._id) {
        dispatch(setSelectBooks({}));
      }
      return;
    }

    const hasSelectedBook =
      !!selectBooks?._id &&
      companyBooks.some((book) => book?._id === selectBooks._id);

    if (!hasSelectedBook) {
      dispatch(
        setSelectBooks({
          _id: companyBooks[0]._id,
          name: companyBooks[0]?.name,
        })
      );
    }
  }, [value, companyBooks, orgBooks, selectBooks?._id, dispatch]);

  React.useEffect(() => {
    if (selectedCompany?._id) return;

    const usersCompany = allCompanies?.data?.companies?.find(
      (item) => item._id === companyId
    );
    if (usersCompany) {
      dispatch(setCompany(usersCompany));
    }
  }, [allCompanies, companyId, dispatch, selectedCompany?._id]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (
    badgeStatsCompanyLoading ||
    userStatsOrgLoading ||
    badgeStatsOrgLoading ||
    userStatsCompanyLoading ||
    companyLoading ||
    isLoading ||
    companyloading
  ) {
    return <ProgressBar />;
  }

  const selectHandler = (company) => {
    dispatch(setSelectBooks({}));
    dispatch(setCompany(company));
    setOpenCompanySelect(false);
  };

  return (
    <div className="container ">
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab
              label="Organization"
              sx={{
                textTransform: "capitalize",
                marginRight: "20px",
                fontWeight: "500",
              }}
              {...a11yProps(0)}
            />

            <Tab
              sx={{ textTransform: "capitalize", fontWeight: "500" }}
              label="Company"
              {...a11yProps(1)}
            />
          </Tabs>
          <div className="dashboard__actions">
            <div className="dashboard__actionsdate">
              <DateRangePickerComp
                onChange={(value) => {
                  if (!value) setDateRange(null);
                  else
                    setDateRange(
                      value.map((val) => new Date(val).toISOString())
                    );
                }}
              />
            </div>
            {/* ?only for superadmin */}
            {/* {value === 1 && (
              <FilterListOutlinedIcon
                sx={{ color: "var(--pblue)", fontSize: "30px" }}
              />
            )} */}

            {value === 1 && (
              <div className="employee__manage_employee_dd">
                <div
                  className={
                    (openCompanySelect ? "open" : "") +
                    " employee__manage_employee_dd_item"
                  }
                >
                  <div
                    onClick={() => {
                      setOpenCompanySelect(!openCompanySelect);
                    }}
                    className="employee__manage_employee_dd_label"
                  >
                    <p>{selectedCompany ? selectedCompany?.name : "Company"}</p>
                    <div>
                      <img
                        src={rightArrow}
                        style={{ marginBottom: "2px" }}
                        alt=""
                      />
                    </div>
                  </div>
                  {openCompanySelect ? (
                    <div className="employee__select_menu">
                      <h1 className="select_divider" />
                      {allCompanies &&
                        allCompanies?.data?.companies.map(
                          (curElm, index) => (
                            <p
                              className="employee__select_menuPara"
                              onClick={() => selectHandler(curElm)}
                              key={index}
                            >
                              {curElm.name}
                            </p>
                          )
                        )}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </Box>
        <TabPanel value={value} index={0}>
          <div className="saadminLayout">
            <div className="sadashboard__content">
              <Graph
                badgeStats={badgeStatsOrg}
                userStats={userStatsOrg}
                bookStats={
                  bookIdForOrgStats
                    ? bookStatsOrg
                    : emptyBookActivityStats()
                }
                bookData={orgBooks}
                isBookStatsRefreshing={
                  !!bookIdForOrgStats && bookStatsOrgFetching
                }
              />

              <QuestActivity userType={"superadmin"} bookData={bookOrg} />
              <div className="questLeaderboard">
                <h3>Leaderboard</h3>
                <LeaderboardTable
                  startDate={dateRange ? dateRange[0] : undefined}
                  endDate={dateRange ? dateRange[1] : undefined}
                />
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <div className="adminLayout">
            <div className="dashboard__content ">
              <Graph
                badgeStats={badgeStatsCompany}
                userStats={userStatsCompany}
                bookStats={
                  bookIdForCompanyStats
                    ? bookStatsCompany
                    : emptyBookActivityStats(
                        totalEmployeesFromUserStats(userStatsCompany)
                      )
                }
                bookData={companyBooks}
                isBookStatsRefreshing={
                  !!bookIdForCompanyStats && bookStatsCompanyFetching
                }
              />
              <QuestActivity userType={"superadmin"} companyBookData={bookCompany} />
              <div className="questLeaderboard">
                <h3>Leaderboard</h3>
                <LeaderboardTable
                  companyId={selectedCompany?._id || ""}
                  startDate={dateRange ? dateRange[0] : undefined}
                  endDate={dateRange ? dateRange[1] : undefined}
                />
              </div>
            </div>
          </div>
        </TabPanel>
      </Box>
    </div>
  );
};
